// src/mixins/gripItemModal.mixin.ts

import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Action, State } from 'vuex-class';
import { ToastProgrammatic as Toast } from 'buefy';
import { isTruthy } from '@/jbi-shared/util/watcher.vue-decorator';
import {
  FileAttachmentDTO,
  GripDomainCategory,
  LinkDTO,
} from '@/store/types/grip.types';
import { ApiState } from '@/store/types/general.types';
import {
  getSourceLink,
  getWordCount,
  validateLink,
} from '@/utils/general.util';
import GenericFileUploader from '@/components/uploader/GenericFileUploader.vue';

/**
 * This mixin requires the component using it to define the following data properties,
 * which the mixin's methods and computed properties will operate on:
 *
 * @property {object} dirtyItem - The main data object for the form (e.g., dirtyGripBarrier).
 * @property {LinkDTO[]} itemLinks - The array of link objects for the UI (e.g., barrierLinks).
 * @property {object | null} itemDetails - The prop containing the initial details for editing.
 */
@Component
export default class GripItemModalMixin extends Vue {
  // --- Props provided by the mixin ---
  @Prop() public projectId!: number;
  @Prop() public isNew!: boolean;
  @Prop({ default: false }) public isModalActive!: boolean;
  @Prop({ default: false }) public isMinimized!: boolean;
  @Prop({ default: true }) public draggable!: boolean;

  // --- Vuex State & Actions provided by the mixin ---
  @Action('projects/getGripDomainCategories')
  public getGripDomainCategories!: (projectId: number) => void;

  @State((state) => state.projects.apiState.getGripDomainCategories)
  public getGripDomainCategoriesState!: ApiState;

  @State((state) => state.projects.gripDomainCategories)
  public gripDomainCategories!: GripDomainCategory[];

  // --- Data properties provided by the mixin ---
  public isDomainCategoriesLoading: boolean = false;
  public gripDomainCategoryOptions: GripDomainCategory[] = [];
  public selectedGripDomainCategoryId: number | null = null;
  public areLinksValid: boolean = true;
  public link: string = ''; // Used for new link input, can be shared
  public count: number = 1; // Used for unique key generation for links
  public fileAttachment: FileAttachmentDTO[] = [];

  public originalDirtyItemState: string = '';
  public originalItemLinksState: string = '';
  public originalFileAttachmentState: string = '';

  // --- Component-provided data (the "contract") ---
  // These are placeholders for type-checking; the actual data must be
  // defined in the component that uses this mixin.
  public dirtyItem!: {
    title: string;
    description: string;
    links: string[];
    attachments: string[];
    gripDomainCategoryId: number | null;
    [key: string]: any; // Allows for other properties like 'reason' or 'action'
  };
  public itemLinks!: LinkDTO[];
  public itemDetails!: {
    gripDomainCategory?: GripDomainCategory | null;
  } | null;

  // --- Computed Properties provided by the mixin ---
  get gripDomainCategoryDetails(): GripDomainCategory | null {
    return this.itemDetails && this.itemDetails.gripDomainCategory
      ? this.itemDetails.gripDomainCategory
      : null;
  }

  get sourceLink(): string {
    return getSourceLink(this.itemLinks);
  }

  get displayModalContent(): string {
    return this.dirtyItem.title;
  }

  get GenericFileUploader() {
    return GenericFileUploader;
  }

  /**
   * Indicates whether the dirty item, links, or file attachments have changed
   * since the component was initialized.
   *
   * @return {boolean} True if any of the dirty item, links, or file attachments
   * have changed since the component was initialized.
   */
  get isDirty(): boolean {
    const dirtyItemChanged =
      JSON.stringify(this.dirtyItem) !== this.originalDirtyItemState;
    const linksChanged =
      JSON.stringify(this.itemLinks) !== this.originalItemLinksState;
    const attachmentsChanged =
      JSON.stringify(this.fileAttachment) !== this.originalFileAttachmentState;

    return dirtyItemChanged || linksChanged || attachmentsChanged;
  }

  // --- Methods provided by the mixin ---
  public wordCount(value: string): number {
    return value.trim().length > 0 ? getWordCount(value.trim()) : 0;
  }

  public handleSelectedGripDomainCategory(
    gripDomainCategory: GripDomainCategory,
  ): void {
    this.selectedGripDomainCategoryId = gripDomainCategory
      ? gripDomainCategory.id
      : null;

    if (this.dirtyItem) {
      this.dirtyItem.gripDomainCategoryId = this.selectedGripDomainCategoryId;
    }
  }

  public addLinkTextInput(): void {
    this.count++;
    this.itemLinks.push({
      url: this.link,
      count: this.count,
      isValid: true,
      errorMessage: '',
    });
  }

  public removeLinkTextInput(index: number): void {
    this.itemLinks.splice(index, 1);
    this.areLinksValid = !this.itemLinks.some((link) => !link.isValid);
  }

  public getGripItemLinks(): void {
    const linkLists: string[] = [];
    this.itemLinks.map((value: LinkDTO) => {
      if (value.url) {
        linkLists.push(value.url);
      }
    });
    this.dirtyItem.links = linkLists;
  }

  public getItemAttachments(): void {
    const updateFileAttachment: string[] = [];
    this.fileAttachment.forEach((element: FileAttachmentDTO) => {
      updateFileAttachment.push(element.type);
    });
    this.dirtyItem.attachments = updateFileAttachment;
  }

  public validateGripLink(index: number): void {
    const link: LinkDTO = validateLink(this.itemLinks[index]);
    this.itemLinks.splice(index, 1, link);
    this.areLinksValid = !this.itemLinks.some((link) => !link.isValid);
  }

  public setInitialFormState(): void {
    // We use $nextTick to ensure that the data from props has been fully
    // processed and is available in `dirtyItem`, `itemLinks`, etc.
    this.$nextTick(() => {
      this.originalDirtyItemState = JSON.stringify(this.dirtyItem);
      this.originalItemLinksState = JSON.stringify(this.itemLinks);
      this.originalFileAttachmentState = JSON.stringify(this.fileAttachment);
    });
  }

  // --- Lifecycle Hooks & Watchers provided by the mixin ---
  public mounted(): void {
    this.isDomainCategoriesLoading = true;
    this.getGripDomainCategories(this.projectId);
  }

  @Watch('getGripDomainCategoriesState.success')
  @isTruthy
  public watchGripDomainCategorySuccess(): void {
    this.isDomainCategoriesLoading = false;
    this.gripDomainCategoryOptions = this.gripDomainCategories
      ? this.gripDomainCategories
      : [];
  }

  @Watch('getGripDomainCategoriesState.error')
  @isTruthy
  public watchGripDomainCategoryError(): void {
    this.isDomainCategoriesLoading = false;
    Toast.open({
      message: `Something went wrong, while fetching Domain/Categories. Try again.`,
      position: 'is-top',
      type: 'is-danger',
      duration: 3500,
    });
  }
}
